import React, { useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { X, ArrowsClockwise, FloppyDisk, Trash, MagicWand, Plus, WarningCircle, Clock } from "@phosphor-icons/react";

const fld = "w-full bg-[#1A1A1A] border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5722] transition-all";
const lbl = "block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const toLocalInput = (iso) => { if (!iso) return ""; const d = new Date(iso); const off = d.getTimezoneOffset(); return new Date(d - off * 60000).toISOString().slice(0, 16); };

export function ScheduleGenerator({ teams, onClose, onSaved }) {
  const { settings } = useSettings();
  const divisions = settings.divisions || [];
  const initDiv = divisions[0] || "Open";
  const [cfg, setCfg] = useState({
    division: initDiv,
    season: settings.hero?.season_title || "Season 2026",
    format: "single",
    start_date: new Date(Date.now() + 3 * 864e5).toISOString().slice(0, 10),
    end_date: "",
    game_days: ["Saturday"],
    game_times: ["18:00"],
    games_per_week: "",
    courts: 1,
    allow_doubleheaders: false,
    playoff_teams: "",
  });
  const [excluded, setExcluded] = useState({}); // team_id -> true when excluded
  const [preview, setPreview] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  const divisionTeams = useMemo(() => teams.filter((t) => (t.division || "Open") === cfg.division), [teams, cfg.division]);
  const selectedTeams = divisionTeams.filter((t) => !excluded[t.id]);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const toggleDay = (d) => setCfg((c) => ({ ...c, game_days: c.game_days.includes(d) ? c.game_days.filter((x) => x !== d) : [...c.game_days, d] }));
  const toggleTeam = (id) => { setExcluded((e) => ({ ...e, [id]: !e[id] })); setPreview(null); };

  const setTime = (i, v) => setCfg((c) => ({ ...c, game_times: c.game_times.map((t, idx) => (idx === i ? v : t)) }));
  const addTime = () => setCfg((c) => ({ ...c, game_times: [...c.game_times, "19:30"] }));
  const removeTime = (i) => setCfg((c) => ({ ...c, game_times: c.game_times.length > 1 ? c.game_times.filter((_, idx) => idx !== i) : c.game_times }));

  const generate = async () => {
    if (cfg.game_days.length === 0) { toast.error("Pick at least one game day"); return; }
    if (cfg.game_times.filter((t) => t).length === 0) { toast.error("Add at least one game time"); return; }
    if (selectedTeams.length < 2) { toast.error("Select at least 2 teams"); return; }
    setBusy(true);
    try {
      const res = await api.post("/schedule/generate", {
        division: cfg.division, season: cfg.season, format: cfg.format,
        start_date: cfg.start_date, end_date: cfg.end_date || "",
        game_days: cfg.game_days, game_times: cfg.game_times.filter((t) => t),
        games_per_week: cfg.games_per_week ? parseInt(cfg.games_per_week, 10) : null,
        courts: parseInt(cfg.courts, 10) || 1, allow_doubleheaders: cfg.allow_doubleheaders,
        team_ids: selectedTeams.map((t) => t.id),
        playoff_teams: cfg.playoff_teams ? parseInt(cfg.playoff_teams, 10) : null,
      });
      setPreview(res.data.games.map((g, i) => ({ ...g, _k: `pv-${i}`, date: toLocalInput(g.date) })));
      setMeta({ teams: res.data.team_count, total: res.data.total_games, weeks: res.data.weeks,
        ends_after_target: res.data.ends_after_target, last_date: res.data.last_date,
        end_date: res.data.end_date, byes: res.data.byes || [], playoff_teams: res.data.playoff_teams });
      toast.success(`Generated ${res.data.total_games} games across ${res.data.weeks.length} weeks`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const updateRow = (idx, key, val) => setPreview((p) => p.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  const removeRow = (idx) => setPreview((p) => p.filter((_, i) => i !== idx));

  const saveAll = async () => {
    if (!preview || preview.length === 0) { toast.error("Nothing to save"); return; }
    const bad = preview.find((r) => !r.home_team_id || !r.away_team_id || r.home_team_id === r.away_team_id || !r.date);
    if (bad) { toast.error("Every game needs two different teams and a date"); return; }
    setBusy(true);
    try {
      await api.post("/games/bulk", {
        games: preview.map((r) => ({
          home_team_id: r.home_team_id, away_team_id: r.away_team_id,
          date: new Date(r.date).toISOString(), venue: r.venue || "",
          status: "scheduled", home_score: 0, away_score: 0, season: r.season || cfg.season,
          court: r.court || "", division: cfg.division, week: r.week, order: r.order || 0,
        })),
      });
      toast.success(`Saved ${preview.length} games`);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const weeks = preview ? Array.from(new Set(preview.map((r) => r.week))).sort((a, b) => a - b) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-2 sm:p-6 bg-black/75 backdrop-blur-sm overflow-y-auto" data-testid="schedule-generator">
      <div className="bg-[#121212] border border-white/10 w-full max-w-4xl my-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#121212] z-10">
          <div className="flex items-center gap-2"><MagicWand size={22} className="text-[#FF5722]" /><h3 className="font-display text-2xl uppercase">IEFSC Schedule Generator</h3></div>
          <button onClick={onClose} data-testid="generator-close" className="text-white/50 hover:text-white"><X size={22} /></button>
        </div>

        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>Division</label>
              <select value={cfg.division} onChange={(e) => { set("division", e.target.value); setPreview(null); setExcluded({}); }} className={fld} data-testid="gen-division">
                {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Season</label>
              <input value={cfg.season} onChange={(e) => set("season", e.target.value)} className={fld} data-testid="gen-season" />
            </div>
            <div>
              <label className={lbl}>Round Robin Format</label>
              <select value={cfg.format} onChange={(e) => set("format", e.target.value)} className={fld} data-testid="gen-format">
                <option value="single">Single (play each once)</option>
                <option value="double">Double (home &amp; away)</option>
              </select>
            </div>
            <div>
              <label className={lbl}>Season Start Date</label>
              <input type="date" value={cfg.start_date} onChange={(e) => set("start_date", e.target.value)} className={fld} data-testid="gen-start-date" />
            </div>
            <div>
              <label className={lbl}>Season End Date (target)</label>
              <input type="date" value={cfg.end_date} onChange={(e) => set("end_date", e.target.value)} className={fld} data-testid="gen-end-date" />
            </div>
            <div>
              <label className={lbl}>Courts / Gyms</label>
              <input type="number" min="1" value={cfg.courts} onChange={(e) => set("courts", e.target.value)} className={fld} data-testid="gen-courts" />
            </div>
            <div>
              <label className={lbl}>Games / Week (optional)</label>
              <input type="number" min="1" value={cfg.games_per_week} onChange={(e) => set("games_per_week", e.target.value)} className={fld} data-testid="gen-games-per-week" placeholder="No cap" />
            </div>
            <div>
              <label className={lbl}>Playoff Teams (optional)</label>
              <input type="number" min="0" value={cfg.playoff_teams} onChange={(e) => set("playoff_teams", e.target.value)} className={fld} data-testid="gen-playoff-teams" placeholder="e.g. 4" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer select-none border border-white/15 px-3 py-2.5 w-full hover:border-white/40 transition-all">
                <input type="checkbox" checked={cfg.allow_doubleheaders} onChange={(e) => set("allow_doubleheaders", e.target.checked)} className="w-4 h-4 accent-[#FF5722]" data-testid="gen-doubleheaders" />
                <span className="text-xs uppercase tracking-wide text-white/70">Allow doubleheaders</span>
              </label>
            </div>
          </div>

          {/* Game days */}
          <div className="mt-4">
            <label className={lbl}>Game Days</label>
            <div className="flex flex-wrap gap-1.5" data-testid="gen-game-days">
              {DAYS.map((d) => (
                <button type="button" key={d} onClick={() => toggleDay(d)} data-testid={`gen-day-${d.toLowerCase()}`}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border transition-all duration-200 ${cfg.game_days.includes(d) ? "bg-[#FF5722] border-[#FF5722] text-black" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                  {d.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Game times */}
          <div className="mt-4">
            <label className={lbl}>Available Game Times <span className="text-white/30 normal-case font-normal">(filled across courts each game day)</span></label>
            <div className="flex flex-wrap items-center gap-2" data-testid="gen-game-times">
              {cfg.game_times.map((t, i) => (
                <div key={i} className="flex items-center gap-1 border border-white/15 bg-[#1A1A1A] pl-2 pr-1 py-1">
                  <Clock size={14} className="text-white/40" />
                  <input type="time" value={t} onChange={(e) => setTime(i, e.target.value)} data-testid={`gen-time-${i}`} className="bg-transparent text-sm focus:outline-none" />
                  {cfg.game_times.length > 1 && <button type="button" onClick={() => removeTime(i)} data-testid={`gen-time-remove-${i}`} className="text-white/30 hover:text-[#FF3B30]"><X size={14} /></button>}
                </div>
              ))}
              <button type="button" onClick={addTime} data-testid="gen-time-add" className="flex items-center gap-1 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/60 px-2.5 py-1.5 text-xs uppercase font-bold"><Plus size={14} /> Time</button>
            </div>
          </div>

          {/* Team selection */}
          <div className="mt-4">
            <label className={lbl}>Teams <span className="text-white/30 normal-case font-normal">(uncheck to exclude · {selectedTeams.length} of {divisionTeams.length} selected)</span></label>
            {divisionTeams.length === 0 ? (
              <p className="text-[#FF3B30] text-xs">No teams in this division. Add teams first.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="gen-team-list">
                {divisionTeams.map((t) => {
                  const on = !excluded[t.id];
                  return (
                    <button type="button" key={t.id} onClick={() => toggleTeam(t.id)} data-testid={`gen-team-${t.id}`}
                      className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border transition-all duration-200 ${on ? "bg-[#FF5722]/15 border-[#FF5722] text-[#FF5722]" : "border-white/15 text-white/35 line-through"}`}>
                      {t.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-white/40 my-4">
            {selectedTeams.length < 2
              ? <span className="text-[#FF3B30]">Select at least 2 teams to generate.</span>
              : <span>{selectedTeams.length} teams · {cfg.format === "double" ? "Double" : "Single"} round robin · {cfg.game_days.length} game day(s)/week · {cfg.game_times.filter((x)=>x).length} time slot(s) × {cfg.courts} court(s)</span>}
          </div>

          <button onClick={generate} disabled={busy || selectedTeams.length < 2} data-testid="gen-generate-btn" className="flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-6 py-3 transition-all duration-200">
            <ArrowsClockwise size={18} weight="bold" /> {preview ? "Regenerate" : "Generate Schedule"}
          </button>

          {preview && (
            <div className="mt-6" data-testid="gen-preview">
              <p className="text-sm text-white/60 mb-3">Review &amp; edit before saving — <span className="text-white font-semibold">{preview.length} games</span> · {meta?.teams} teams · {weeks.length} weeks</p>

              {meta?.ends_after_target && (
                <div className="flex items-start gap-2 text-sm border border-[#FFB300]/40 bg-[#FFB300]/10 p-3 mb-3" data-testid="gen-endswarn">
                  <WarningCircle size={18} weight="fill" className="text-[#FFB300] shrink-0 mt-0.5" />
                  <span className="text-white/80">The last game ({meta.last_date?.slice(0, 10)}) runs past your target end date ({meta.end_date}). Add more game days/courts or extend the season.</span>
                </div>
              )}
              {meta?.byes?.length > 0 && (
                <div className="text-xs text-white/50 border border-white/10 bg-[#0F0F0F] p-3 mb-3" data-testid="gen-byes">
                  <span className="uppercase tracking-wide text-white/40 font-bold">Bye weeks:</span>{" "}
                  {meta.byes.map((b) => `Week ${b.week} — ${b.teams.join(", ")}`).join(" · ")}
                </div>
              )}
              {meta?.playoff_teams ? (
                <div className="text-xs text-white/50 border border-white/10 bg-[#0F0F0F] p-3 mb-3" data-testid="gen-playoff-note">
                  <span className="uppercase tracking-wide text-[#FF5722] font-bold">Playoffs:</span> Top {meta.playoff_teams} teams captured — the playoff bracket can be generated from final standings later.
                </div>
              ) : null}

              <div className="space-y-4 max-h-[46vh] overflow-y-auto pr-1">
                {weeks.map((wk) => (
                  <div key={wk} data-testid={`gen-week-${wk}`}>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5722] mb-1.5">Week {wk}</div>
                    <div className="border border-white/10 divide-y divide-white/5">
                      {preview.map((r, i) => r.week === wk && (
                        <div key={r._k || i} className="flex flex-col sm:flex-row sm:items-center gap-2 p-2.5 bg-[#0F0F0F]" data-testid={`gen-row-${i}`}>
                          <select value={r.home_team_id} onChange={(e) => updateRow(i, "home_team_id", e.target.value)} className="flex-1 bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm">
                            {selectedTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                          <span className="text-[#FF5722] font-bold text-xs px-1">vs</span>
                          <select value={r.away_team_id} onChange={(e) => updateRow(i, "away_team_id", e.target.value)} className="flex-1 bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm">
                            {selectedTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                          </select>
                          <input type="datetime-local" value={r.date} onChange={(e) => updateRow(i, "date", e.target.value)} className="bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm" />
                          <input value={r.court || ""} onChange={(e) => updateRow(i, "court", e.target.value)} placeholder="Court" className="bg-[#1A1A1A] border border-white/15 px-2 py-2 text-sm w-24" />
                          <button onClick={() => removeRow(i)} className="text-white/30 hover:text-[#FF3B30] transition-colors self-end sm:self-auto"><Trash size={16} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mt-4">
                <button onClick={generate} disabled={busy} className="flex items-center gap-2 border border-white/15 hover:border-white text-white/80 font-bold uppercase tracking-wide px-5 py-3 transition-all duration-200" data-testid="gen-regenerate-btn">
                  <ArrowsClockwise size={18} /> Regenerate
                </button>
                <button onClick={saveAll} disabled={busy} data-testid="gen-save-btn" className="flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-8 py-3 transition-all duration-200 ml-auto">
                  <FloppyDisk size={18} weight="bold" /> {busy ? "Saving..." : `Save ${preview.length} Games`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
